/*
  i18n — vier Sprachen, EN als Primärsprache.
  EN = Referenz-Dictionary (Typquelle), DE/SW (Kiswahili)/SH (Sheng)
  müssen dieselbe Struktur vollständig füllen — kein stilles Fallback,
  fehlende Keys brechen den Build.
  Persistenz: localStorage + ?lang= Query-Param. <html lang> wird gesetzt.
*/
import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { en } from "@/locales/en";

export type Lang = "en" | "de" | "sw" | "sh";
export type Dict = typeof en;

export const LANGS: { code: Lang; label: string; htmlLang: string; ogLocale: string }[] = [
  { code: "en", label: "EN", htmlLang: "en", ogLocale: "en_US" },
  { code: "de", label: "DE", htmlLang: "de-CH", ogLocale: "de_CH" },
  { code: "sw", label: "SW", htmlLang: "sw-KE", ogLocale: "sw_KE" },
  { code: "sh", label: "SHENG", htmlLang: "sw-KE", ogLocale: "sw_KE" },
];

const DICT_LOADERS: Record<Exclude<Lang, "en">, () => Promise<Dict>> = {
  de: () => import("@/locales/de").then((module) => module.de),
  sw: () => import("@/locales/sw").then((module) => module.sw),
  sh: () => import("@/locales/sh").then((module) => module.sh),
};
const STORAGE_KEY = "bearth-lang";

function initialLang(): Lang {
  try {
    const param = new URLSearchParams(window.location.search).get("lang");
    if (param && ["en", "de", "sw", "sh"].includes(param)) return param as Lang;
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && ["en", "de", "sw", "sh"].includes(stored)) return stored as Lang;
    const nav = navigator.language?.toLowerCase() ?? "";
    if (nav.startsWith("de")) return "de";
    if (nav.startsWith("sw")) return "sw";
  } catch {
    /* SSR/Privacy-Modus: Default greift */
  }
  return "en";
}

interface I18nCtx {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: Dict;
}

const Ctx = createContext<I18nCtx>({ lang: "en", setLang: () => {}, t: en });

export function I18nProvider({ children }: { children: ReactNode }) {
  /* Start with the prerendered EN source of truth so hydration stays stable;
     a stored or browser-derived language is applied after first paint. */
  const [lang, setLangState] = useState<Lang>("en");
  const [t, setDictionary] = useState<Dict>(en);

  useEffect(() => {
    setLangState(initialLang());
  }, []);

  useEffect(() => {
    const meta = LANGS.find((l) => l.code === lang)!;
    document.documentElement.lang = meta.htmlLang;
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      /* Speichern optional */
    }
  }, [lang]);

  useEffect(() => {
    let current = true;
    if (lang === "en") {
      setDictionary(en);
      return () => { current = false; };
    }
    void DICT_LOADERS[lang]().then((dictionary) => {
      if (current) setDictionary(dictionary);
    });
    return () => { current = false; };
  }, [lang]);

  return <Ctx.Provider value={{ lang, setLang: setLangState, t }}>{children}</Ctx.Provider>;
}

export function useI18n() {
  return useContext(Ctx);
}
